import {
  DISCOUNT_SCOPES,
  DISCOUNT_TYPES,
  type BuyXGetYItem,
  type Discount,
  type DiscountInput,
  type DiscountRules,
  type DiscountScope,
  type DiscountTier,
  type DiscountType,
  type SpecialPriceItem,
} from '@/domain/entities/discount.entity'
import type { DiscountDto } from './discount.dto'

/** Nilai asing dari jaringan tidak boleh membuat seluruh tabel gagal render. */
function toType(raw: string): DiscountType {
  return (DISCOUNT_TYPES as readonly string[]).includes(raw) ? (raw as DiscountType) : 'amount'
}

function toScope(raw: string): DiscountScope {
  return (DISCOUNT_SCOPES as readonly string[]).includes(raw) ? (raw as DiscountScope) : 'both'
}

function toRules(raw: Record<string, unknown> | null | undefined): DiscountRules {
  if (!raw) return {}
  return {
    min_purchase_amount:
      typeof raw.min_purchase_amount === 'number' ? raw.min_purchase_amount : null,
    discount_type:
      typeof raw.discount_type === 'string' &&
      (raw.discount_type === 'percent' || raw.discount_type === 'amount')
        ? raw.discount_type
        : null,
    usage_limit: typeof raw.usage_limit === 'number' ? raw.usage_limit : null,
    buy_product_sku: typeof raw.buy_product_sku === 'string' ? raw.buy_product_sku : null,
    buy_product_skus: Array.isArray(raw.buy_product_skus)
      ? (raw.buy_product_skus as string[])
      : null,
    buy_qty: typeof raw.buy_qty === 'number' ? raw.buy_qty : null,
    get_product_sku: typeof raw.get_product_sku === 'string' ? raw.get_product_sku : null,
    get_product_skus: Array.isArray(raw.get_product_skus)
      ? (raw.get_product_skus as string[])
      : null,
    get_qty: typeof raw.get_qty === 'number' ? raw.get_qty : null,
    get_discount_percent:
      typeof raw.get_discount_percent === 'number' ? raw.get_discount_percent : null,
    is_multiple: typeof raw.is_multiple === 'boolean' ? raw.is_multiple : true,
    buy_x_get_y_items: Array.isArray(raw.buy_x_get_y_items)
      ? (raw.buy_x_get_y_items as BuyXGetYItem[])
      : null,
    target_product_sku: typeof raw.target_product_sku === 'string' ? raw.target_product_sku : null,
    special_price: typeof raw.special_price === 'number' ? raw.special_price : null,
    special_price_items: Array.isArray(raw.special_price_items)
      ? (raw.special_price_items as SpecialPriceItem[])
      : null,
    tiers: Array.isArray(raw.tiers) ? (raw.tiers as DiscountTier[]) : null,
  }
}

function baseBody(input: DiscountInput) {
  return {
    name: input.name,
    type: input.type,
    value: input.value,
    scope: input.scope,
    require_reason: input.requireReason,
    is_all_outlets: input.isAllOutlets,
    outlet_ids: input.isAllOutlets ? [] : input.outletIds,
    ...(input.couponCode?.trim() ? { coupon_code: input.couponCode.trim() } : {}),
    rules: input.rules,
  }
}

export const discountMapper = {
  toDiscount(dto: DiscountDto): Discount {
    return {
      id: dto.id,
      tenantId: dto.tenant_id,
      name: dto.name,
      type: toType(dto.type),
      value: dto.value ?? 0,
      // 0 dari BE tidak mungkin (ditolak saat menulis), tapi null jelas berarti
      // "tanpa batas" — jangan diterjemahkan jadi angka.
      maxAmount: typeof dto.max_amount === 'number' ? dto.max_amount : null,
      couponCode: dto.coupon_code ?? null,
      isAllOutlets: dto.is_all_outlets ?? false,
      outletIds: Array.isArray(dto.outlet_ids) ? dto.outlet_ids : [],
      rules: toRules(dto.rules),
      scope: toScope(dto.scope),
      requireReason: dto.require_reason ?? false,
      active: dto.active ?? true,
    }
  },

  /** POST: `max_amount` hanya dikirim kalau memang ada batasnya. */
  toCreateBody(input: DiscountInput) {
    return {
      ...baseBody(input),
      ...(input.maxAmount !== null ? { max_amount: input.maxAmount } : {}),
    }
  },

  /**
   * PUT: `max_amount` yang tidak dikirim dan yang dikirim `null` sama-sama tiba
   * di BE sebagai pointer nil, dan nil selalu berarti "pertahankan yang lama".
   * Jadi menghapus batas HARUS lewat `clear_max_amount`. Tanpa ini, diskon
   * persen ber-`max_amount` tidak akan pernah bisa diubah menjadi `amount`:
   * validasi gabungan akan menolaknya 400 berulang kali tanpa jalan keluar.
   *
   * Keduanya tidak pernah dikirim bersamaan — BE menolak kombinasi itu 400.
   */
  toUpdateBody(input: DiscountInput) {
    return {
      ...baseBody(input),
      ...(input.maxAmount !== null ? { max_amount: input.maxAmount } : { clear_max_amount: true }),
    }
  },
}
